/**
 * Operator-visible channel selling price. Presentation only.
 * Authority: intendedProviderPrice (listing strategy + listing-variant channel_price).
 * Does not write prices, call providers, or treat last_published_channel_price as live confirmed.
 */
import { money } from "@/lib/format";
import { effectiveSellingPrice } from "@/lib/products";
import {
  computeChannelPrice,
  formatPriceAdjustmentLine,
  intendedProviderPrice,
  pricingConfigFromProviderConfig,
  type PriceStrategyConfig,
} from "@/lib/listing-pricing";
import type { PriceStrategy } from "@/lib/channels";

export type ChannelPriceKind = "override" | "inherited" | "strategy";

export type VariantChannelPriceRow = {
  variantId: string;
  sku: string | null;
  options: string;
  canonicalBase: number | null;
  strategyPrice: number | null;
  strategyAdjustment: string | null;
  listingOverride: number | null;
  effective: number | null;
  kind: ChannelPriceKind;
};

export type ListingChannelPriceSummary = {
  label: string;
  min: number | null;
  max: number | null;
  mixed: boolean;
  overrideCount: number;
  pending: boolean;
  pendingLabel: string | null;
};

function finitePrice(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatStoredMoney(value: number | null | undefined, currency?: string | null): string {
  const n = finitePrice(value);
  if (n == null) return "—";
  return money(n, currency);
}

export function summarizeEffectiveChannelPrices(
  amounts: Array<number | null | undefined>,
  currency?: string | null,
): { kind: "empty" | "single" | "range"; min: number | null; max: number | null; label: string } {
  const nums = amounts.map(finitePrice).filter((n): n is number => n != null);
  if (nums.length === 0) return { kind: "empty", min: null, max: null, label: "—" };
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  if (min === max) return { kind: "single", min, max, label: formatStoredMoney(min, currency) };
  return {
    kind: "range",
    min,
    max,
    label: `${formatStoredMoney(min, currency)} – ${formatStoredMoney(max, currency)}`,
  };
}

export function resolveVariantEffectiveChannelPrice(opts: {
  priceStrategy: PriceStrategy;
  pricingConfig?: PriceStrategyConfig;
  productSellingPrice: number | null | undefined;
  variantSellingPrice: number | null | undefined;
  channelPrice?: number | null;
}): {
  canonicalBase: number | null;
  strategyPrice: number | null;
  listingOverride: number | null;
  effective: number | null;
  kind: ChannelPriceKind;
} {
  const canonicalBase = effectiveSellingPrice(opts.variantSellingPrice ?? null, opts.productSellingPrice ?? null);
  const listingOverride = finitePrice(opts.channelPrice);
  const cfg = opts.pricingConfig ?? {};
  const strategyPrice = canonicalBase == null ? null : computeChannelPrice(opts.priceStrategy, canonicalBase, cfg);
  if (listingOverride != null) {
    return {
      canonicalBase,
      strategyPrice,
      listingOverride,
      effective: intendedProviderPrice(opts.priceStrategy, canonicalBase ?? listingOverride, cfg, listingOverride),
      kind: "override",
    };
  }
  if (canonicalBase == null) {
    return { canonicalBase: null, strategyPrice: null, listingOverride: null, effective: null, kind: "inherited" };
  }
  const effective = intendedProviderPrice(opts.priceStrategy, canonicalBase, cfg, null);
  const kind: ChannelPriceKind = opts.priceStrategy === "inherit" ? "inherited" : "strategy";
  return { canonicalBase, strategyPrice, listingOverride: null, effective, kind };
}

export function variantChannelPriceKindLabel(kind: ChannelPriceKind): string {
  if (kind === "override") return "Override";
  if (kind === "strategy") return "Channel rule";
  return "Inherited";
}

/**
 * last_published_channel_price is restore authority for a temporary listing
 * override. It is not last provider-confirmed live price.
 */
export function lastPublishedChannelPriceIsRestoreAuthority(): true {
  return true;
}

/** No trustworthy stored current provider-confirmed amount on the listing row. */
export function storedProviderConfirmedChannelPrice(_unused?: unknown): number | null {
  return null;
}

export function channelPriceSyncHint(pricingDirty: boolean): string | null {
  return pricingDirty ? "Price sync pending" : null;
}

export function buildVariantChannelPriceRow(opts: {
  variantId: string;
  sku?: string | null;
  optionValues?: Record<string, string> | null;
  priceStrategy: PriceStrategy;
  pricingConfig?: PriceStrategyConfig;
  productSellingPrice: number | null | undefined;
  variantSellingPrice: number | null | undefined;
  channelPrice?: number | null;
}): VariantChannelPriceRow {
  const resolved = resolveVariantEffectiveChannelPrice(opts);
  const options = opts.optionValues
    ? Object.values(opts.optionValues).filter(Boolean).join(" / ")
    : "";
  return {
    variantId: opts.variantId,
    sku: opts.sku ?? null,
    options: options || "Default",
    canonicalBase: resolved.canonicalBase,
    strategyPrice: resolved.strategyPrice,
    strategyAdjustment:
      resolved.kind === "strategy" || (resolved.kind === "override" && opts.priceStrategy !== "inherit")
        ? formatPriceAdjustmentLine(opts.priceStrategy, opts.pricingConfig ?? {})
        : opts.priceStrategy === "inherit"
          ? "Same as base"
          : formatPriceAdjustmentLine(opts.priceStrategy, opts.pricingConfig ?? {}),
    listingOverride: resolved.listingOverride,
    effective: resolved.effective,
    kind: resolved.kind,
  };
}

export function listingPriceRowsFromLocalState(opts: {
  priceStrategy: PriceStrategy;
  providerConfig?: Record<string, unknown> | null;
  productSellingPrice: number | null | undefined;
  variants: Array<{
    variant_id?: string;
    included?: boolean;
    channel_price?: number | null;
    variant?: {
      id?: string;
      sku?: string | null;
      selling_price?: number | null;
      option_values?: Record<string, string> | null;
    } | null;
    variants?: {
      id?: string;
      sku?: string | null;
      selling_price?: number | null;
      option_values?: Record<string, string> | null;
    } | Array<{
      id?: string;
      sku?: string | null;
      selling_price?: number | null;
      option_values?: Record<string, string> | null;
    }> | null;
  }>;
}): VariantChannelPriceRow[] {
  const cfg = pricingConfigFromProviderConfig(opts.providerConfig);
  const included = opts.variants.filter((v) => v.included !== false);
  if (included.length === 0) {
    return [
      buildVariantChannelPriceRow({
        variantId: "listing",
        sku: null,
        optionValues: null,
        priceStrategy: opts.priceStrategy,
        pricingConfig: cfg,
        productSellingPrice: opts.productSellingPrice,
        variantSellingPrice: null,
        channelPrice: null,
      }),
    ];
  }
  return included.map((v, i) => {
    const nestedRaw = v.variant ?? v.variants ?? null;
    const nested = Array.isArray(nestedRaw) ? nestedRaw[0] ?? null : nestedRaw;
    return buildVariantChannelPriceRow({
      variantId: String(nested?.id || v.variant_id || i),
      sku: nested?.sku ?? null,
      optionValues: nested?.option_values ?? null,
      priceStrategy: opts.priceStrategy,
      pricingConfig: cfg,
      productSellingPrice: opts.productSellingPrice,
      variantSellingPrice: nested?.selling_price ?? null,
      channelPrice: v.channel_price ?? null,
    });
  });
}

export function summarizeListingChannelPrices(opts: {
  rows: Array<{ effective: number | null; kind: ChannelPriceKind }>;
  pricingDirty?: boolean;
  currency?: string | null;
}): ListingChannelPriceSummary {
  const summary = summarizeEffectiveChannelPrices(
    opts.rows.map((r) => r.effective),
    opts.currency,
  );
  const overrideCount = opts.rows.filter((r) => r.kind === "override").length;
  const pending = Boolean(opts.pricingDirty);
  return {
    label: summary.label,
    min: summary.min,
    max: summary.max,
    mixed: summary.kind === "range",
    overrideCount,
    pending,
    pendingLabel: channelPriceSyncHint(pending),
  };
}
